import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import { decodeJwtSub } from '../../utils/jwt';
import { getOrCreateDeviceId } from '../../utils/deviceId';

// ═══════════════════════════════════════════════════════════════════════
//  User Activity Tracker
// ═══════════════════════════════════════════════════════════════════════
// Tracks the last real user interaction (mouse / keyboard / click) so the
// /api/auth/refresh interceptor can attach an `X-User-Activity` header
// with the epoch-seconds timestamp. The auth Lambda only bumps its
// idle-TTL row when the timestamp is recent (< 10 min), which means
// setTimeout-driven proactive refreshes on an abandoned tab do NOT
// keep the session alive.

let _lastUserActivityAt = Math.floor(Date.now() / 1000);   // seconds
let _lastActivityWriteAt = 0;                              // ms; for 30 s debounce
const _ACTIVITY_DEBOUNCE_MS = 30_000;

function _recordActivity() {
  const nowMs = Date.now();
  if (nowMs - _lastActivityWriteAt < _ACTIVITY_DEBOUNCE_MS) return;
  _lastActivityWriteAt = nowMs;
  _lastUserActivityAt = Math.floor(nowMs / 1000);
}

if (typeof window !== 'undefined') {
  // Passive listeners — never block the page; cheap. The three events
  // collectively cover almost every legitimate "user is here" signal
  // (touchstart maps to mousedown on iOS/Android via the browser).
  window.addEventListener('mousemove', _recordActivity, { passive: true });
  window.addEventListener('keydown', _recordActivity, { passive: true });
  window.addEventListener('click', _recordActivity, { passive: true });
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * API Client — JWT Auth with Silent Refresh (OWASP-compliant)
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Security architecture:
 *   1. ID Token stored in sessionStorage (or localStorage with Remember Me)
 *      + in-memory variable for Bearer header.
 *   2. httpOnly cookies carry tokens via the FastAPI auth proxy (same-origin).
 *   3. On 401 → single-flight silent refresh → queue + retry all failed reqs.
 *   4. Proactive refresh 5 min before expiry prevents most 401s.
 *   5. Cross-tab auth sync via localStorage `storage` event.
 *
 * OWASP JWT Best Practices:
 *   • Short-lived access tokens (1h, Cognito-managed)
 *   • Refresh via httpOnly cookie (not accessible to XSS)
 *   • Proactive refresh minimizes expired-token requests
 *   • No infinite refresh loops (_retry flag per request)
 *   • Concurrent 401s coalesce on a single refresh promise
 *
 * Libraries: axios (MIT license)
 */

// ═══════════════════════════════════════════════════════════════════════
//  Global Auth Failure Handler
// ═══════════════════════════════════════════════════════════════════════

/** Flag to prevent multiple simultaneous redirects to /login. */
let _isRedirectingToLogin = false;

/**
 * Called when a 401 occurs AND the refresh attempt also fails.
 * Clears all auth state and redirects to /login.
 * Guarded against duplicate redirects (multiple 401s arriving simultaneously).
 */
function _handleAuthFailure() {
  if (_isRedirectingToLogin) return;
  _isRedirectingToLogin = true;

  console.warn('[API Client] Auth failure — session expired, redirecting to login');

  // Clear this tab's in-memory + storage token
  _inMemoryToken = null;
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  cancelTokenRefresh();

  // Mark this tab as "auth failed" so HomeRoute doesn't re-check and loop
  sessionStorage.setItem('__ak_auth_failed', '1');

  // NOTE: We intentionally do NOT clear shared localStorage session metadata
  // or broadcast 'logout' here. This is an automatic auth failure (e.g. token
  // expired + refresh failed), NOT a user-initiated sign-out. Other tabs may
  // still have valid sessions and should not be affected.
  // User-initiated logout (signOut → clearAuthToken) still broadcasts properly.

  // Redirect — use replace so the user can't "back" into the stale page
  window.location.replace('/login');
}

// ═══════════════════════════════════════════════════════════════════════
//  Token Storage
// ═══════════════════════════════════════════════════════════════════════

const TOKEN_KEY = '__ak_id_token';

/**
 * Token storage backend.
 *
 * ALWAYS use localStorage so the JWT is accessible from every tab in the
 * same browser.  Previously, non-Remember-Me logins used sessionStorage,
 * which is tab-scoped — opening the site in a new tab therefore had NO
 * Bearer token to send, the backend returned 401, and the refresh
 * call also failed, logging the user out.
 *
 * Session lifetime is controlled by the httpOnly refresh cookie
 * set via the FastAPI auth proxy (8 h without Remember Me / 365 d with it).  The
 * short-lived id-token in localStorage (1 h) cannot extend a session on
 * its own, so the security model is unchanged.
 */
function _getTokenStorage() {
  return localStorage;
}

let _inMemoryToken = (() => {
  // Check both storages on startup (user may have toggled remember-me)
  return localStorage.getItem(TOKEN_KEY) ||
         sessionStorage.getItem(TOKEN_KEY) ||
         null;
})();

/**
 * Store the JWT ID token for API requests.
 * @param {string|null} token - Cognito ID token (JWT string)
 */
export const setAuthToken = (token) => {
  _inMemoryToken = token || null;
  const storage = _getTokenStorage();

  if (token) {
    storage.setItem(TOKEN_KEY, token);
    // Clean up the other storage to avoid stale tokens
    const other = storage === localStorage ? sessionStorage : localStorage;
    other.removeItem(TOKEN_KEY);
  } else {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  }
};

/**
 * Get the current JWT token.
 * @returns {string|null}
 */
export const getAuthToken = () => _inMemoryToken;

/**
 * Clear the token (call on sign-out).
 */
export const clearAuthToken = () => {
  _inMemoryToken = null;
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  cancelTokenRefresh();

  // Clear ALL session metadata so route guards don't see stale data
  try {
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  } catch { /* non-critical */ }

  // Broadcast logout to other tabs
  _broadcastAuthEvent('logout');
};

// ═══════════════════════════════════════════════════════════════════════
//  Cross-Tab Auth Sync (via localStorage storage event)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Broadcast an auth lifecycle event to other tabs.
 * Uses localStorage write + remove trick — works in ALL browsers including
 * Safari < 15.4 which lacks BroadcastChannel.
 *
 * @param {'login'|'logout'|'token_refreshed'} eventType
 */
function _broadcastAuthEvent(eventType) {
  try {
    const payload = JSON.stringify({ type: eventType, ts: Date.now() });
    localStorage.setItem(STORAGE_KEYS.AUTH_EVENT, payload);
    // Remove immediately — we only need the `storage` event to fire in other tabs
    localStorage.removeItem(STORAGE_KEYS.AUTH_EVENT);
  } catch {
    // localStorage may be full or blocked — non-critical
  }
}

/**
 * Listen for auth events from other tabs.
 * Initialised once at module load time.
 */
function _initCrossTabAuthSync() {
  if (typeof window === 'undefined') return;

  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEYS.AUTH_EVENT || !e.newValue) return;

    try {
      const event = JSON.parse(e.newValue);

      switch (event.type) {
        case 'logout':
          // Another tab logged out — clear local state and redirect
          _inMemoryToken = null;
          localStorage.removeItem(TOKEN_KEY);
          sessionStorage.removeItem(TOKEN_KEY);
          cancelTokenRefresh();
          // Clear all session metadata
          try {
            Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
          } catch { /* non-critical */ }
          window.location.replace('/login');
          break;

        case 'login':
        case 'token_refreshed': {
          // Another tab logged in or refreshed — pick up the new token.
          const freshToken = localStorage.getItem(TOKEN_KEY) ||
                             sessionStorage.getItem(TOKEN_KEY);
          if (!freshToken) break;

          // F3 — cross-tab identity guard.
          // If the new token's `sub` differs from our currently-rendered
          // userId, this is a cross-tab identity swap (e.g. another tab
          // logged in as a different user). Don't silently adopt it —
          // force a clean re-render against the new identity by signing
          // out + reloading. The next page-load will go through HomeRoute
          // and render the right user (or login if no longer authed).
          try {
            const newSub = decodeJwtSub(freshToken);
            const storedUserId = localStorage.getItem(STORAGE_KEYS.USER_ID);
            if (newSub && storedUserId && newSub !== storedUserId) {
              console.warn('[CrossTab] identity swap detected — reloading', {
                storedUserId,
                newSub,
              });
              import('../auth/authApi').then(({ signOut }) => {
                Promise.resolve(signOut()).finally(() => window.location.reload());
              }).catch(() => window.location.reload());
              break;
            }
          } catch { /* fail open — if decode/grep fails, accept the token */ }

          _inMemoryToken = freshToken;
          scheduleTokenRefresh();
          break;
        }

        default:
          break;
      }
    } catch {
      // Malformed event — ignore
    }
  });
}

_initCrossTabAuthSync();

// ═══════════════════════════════════════════════════════════════════════
//  Single-Flight Token Refresh with Request Coalescing
// ═══════════════════════════════════════════════════════════════════════

/** Timer ID for the proactive refresh scheduler. */
let _refreshTimerId = null;

/** Promise for the in-flight refresh (null if idle). */
let _refreshPromise = null;

/** How many seconds before expiry to trigger the proactive refresh. */
const REFRESH_BUFFER_SECONDS = 5 * 60; // 5 minutes

/**
 * Attempt to refresh the session tokens via /auth/refresh.
 *
 * CRITICAL: This is single-flight. If a refresh is already in progress,
 * all callers receive the SAME promise. This prevents:
 *   - Multiple parallel /auth/refresh calls
 *   - Race conditions where one caller gets a stale token
 *   - Thundering herd on 401s from concurrent requests
 *
 * @returns {Promise<boolean>} true on success, false on failure
 */
async function _doRefresh() {
  // If a refresh is already in-flight, piggyback on it
  if (_refreshPromise) {
    return _refreshPromise;
  }

  _refreshPromise = (async () => {
    try {
      const { refreshSession } = await import('../auth/authApi');
      const result = await refreshSession();

      if (result?.idToken) {
        // F3b — same-tab refresh-response identity guard.
        // If the refresh response carries an idToken whose `sub`
        // differs from the userId we have stored, the server returned
        // tokens for a different user (server bug or session
        // confusion). Fail closed: sign out + redirect to /login.
        // Do NOT retry the in-flight request with the new token.
        try {
          const newSub = decodeJwtSub(result.idToken);
          const storedUserId = localStorage.getItem(STORAGE_KEYS.USER_ID);
          if (newSub && storedUserId && newSub !== storedUserId) {
            console.warn('[Refresh] response carries different sub — forcing relogin', {
              storedUserId,
              newSub,
            });
            const { signOut } = await import('../auth/authApi');
            await signOut();
            window.location.replace('/login');
            return false;
          }
        } catch { /* fail open on decode error */ }

        console.debug('[API Client] Token refreshed successfully');
        scheduleTokenRefresh();
        _broadcastAuthEvent('token_refreshed');
        return true;
      }
      return false;
    } catch (err) {
      console.warn('[API Client] Token refresh failed:', err.message);
      // Surface IDLE_TIMEOUT to the login page so the user gets a clear
      // "signed out due to inactivity" toast instead of an unexplained
      // redirect.  Other 401 codes (IDLE_TIMEOUT_OR_INVALID, AUTH_FAILED)
      // are silent — they happen for normal expired/revoked sessions.
      try {
        if (err?.code === 'IDLE_TIMEOUT' && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('__ak_logout_reason', 'IDLE_TIMEOUT');
        }
      } catch { /* SSR / privacy mode */ }
      return false;
    } finally {
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
}

/**
 * Schedule a proactive token refresh based on the stored TOKEN_EXPIRY.
 * Should be called after login and after every successful refresh.
 */
export function scheduleTokenRefresh() {
  cancelTokenRefresh();

  const expiryStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
  if (!expiryStr) return;

  const expiryMs = parseInt(expiryStr, 10) * 1000; // stored in seconds → ms
  const now = Date.now();
  const msUntilRefresh = expiryMs - now - REFRESH_BUFFER_SECONDS * 1000;

  if (msUntilRefresh <= 0) {
    _doRefresh();
    return;
  }

  console.debug(
    `[API Client] Next token refresh in ${Math.round(msUntilRefresh / 1000)}s ` +
    `(token expires in ${Math.round((expiryMs - now) / 1000)}s)`
  );

  _refreshTimerId = setTimeout(() => {
    _doRefresh();
  }, msUntilRefresh);
}

/**
 * Cancel any pending proactive refresh timer.
 */
export function cancelTokenRefresh() {
  if (_refreshTimerId) {
    clearTimeout(_refreshTimerId);
    _refreshTimerId = null;
  }
}

/**
 * Get the current refresh promise (for WebSocket / polling consumers).
 * Returns null if no refresh is in-flight.
 * @returns {Promise<boolean>|null}
 */
export function getRefreshPromise() {
  return _refreshPromise;
}

/**
 * Trigger a token refresh and return success status.
 * If a refresh is already in-flight, shares the same promise.
 * Used by WebSocket and polling subsystems that detect auth failure.
 * @returns {Promise<boolean>}
 */
export function requestTokenRefresh() {
  return _doRefresh();
}

// ═══════════════════════════════════════════════════════════════════════
//  Axios Instance
// ═══════════════════════════════════════════════════════════════════════

/**
 * AUTH STRATEGY (unified, no competing systems):
 * 1. In-memory token → Bearer header (request interceptor)
 * 2. httpOnly cookies → same-origin via FastAPI auth proxy (withCredentials)
 * 3. On 401 → single-flight refresh → retry
 */
const apiClient = axios.create({
  baseURL: API_CONFIG.BACKEND_URL,
  timeout: API_CONFIG.TIMEOUT,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor — attach Bearer token
 */
apiClient.interceptors.request.use(
  (config) => {
    if (_inMemoryToken) {
      config.headers.Authorization = `Bearer ${_inMemoryToken}`;
    }
    // Attach last-real-activity epoch so /api/auth/refresh can
    // distinguish active sessions from setTimeout-driven proactive
    // refreshes on abandoned tabs.
    config.headers['X-User-Activity'] = String(_lastUserActivityAt);
    // Propagate or generate a correlation ID so every request in a user
    // action chain can be traced end-to-end across backend audit logs.
    if (!config.headers['X-Correlation-Id']) {
      config.headers['X-Correlation-Id'] =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `fe-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    // STAFF-DEVICE-LOCK — stable per-browser device id, read from
    // localStorage at most once per page load (memoized in deviceId.js).
    config.headers['X-Device-Id'] = getOrCreateDeviceId();
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor — silent refresh on 401
 *
 * On 401:
 *   1. Mark request as _retry to prevent infinite loop
 *   2. Await single-flight _doRefresh() (concurrent 401s share same promise)
 *   3. On success → retry original request with fresh token
 *   4. On failure → reject (page/context handles sign-out)
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshed = await _doRefresh();

        if (refreshed && _inMemoryToken) {
          originalRequest.headers.Authorization = `Bearer ${_inMemoryToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('[API Client] Token refresh on 401 failed:', refreshError.message);
        _handleAuthFailure();
        return Promise.reject(error);
      }

      // Refresh returned false (failed silently) — force logout
      _handleAuthFailure();
    }

    if (error.response?.status === 403) {
      // B2 / FE-0 — ADMISSION_DISABLED self-heal (multi-tab stale catalog).
      // B1's router gate answers 403 {detail:{code:"ADMISSION_DISABLED"}} on
      // every IPD route once the org flag is flipped off elsewhere; force a
      // refetch of the flag so <AdmissionEnabledGate> redirects. Keys STRICTLY
      // on the machine-readable code — every other 403 is untouched. Dynamic
      // import avoids a static module cycle (orgSettingsStore →
      // admissionSettingsApi → apiClient). Best-effort: the error still
      // propagates to the caller unchanged.
      if (error.response?.data?.detail?.code === 'ADMISSION_DISABLED') {
        import('../../stores/orgSettingsStore')
          .then((mod) => mod.default.getState().onAdmissionDisabled())
          .catch(() => { /* self-heal is best-effort */ });
      }
      console.error('[API Client] Forbidden — insufficient permissions');
    } else if (error.response?.status >= 500) {
      console.error('[API Client] Server error:', error.response.status);
    }

    return Promise.reject(error);
  }
);

/**
 * Create a multipart/form-data axios instance for file uploads.
 * Shares the same auth + retry strategy as apiClient.
 */
export const createFormDataClient = () => {
  const formDataClient = axios.create({
    baseURL: API_CONFIG.BACKEND_URL,
    timeout: API_CONFIG.TIMEOUT,
    withCredentials: true,
    headers: {},
  });

  formDataClient.interceptors.request.use(
    (config) => {
      if (_inMemoryToken) {
        config.headers.Authorization = `Bearer ${_inMemoryToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  formDataClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const refreshed = await _doRefresh();
          if (refreshed && _inMemoryToken) {
            originalRequest.headers.Authorization = `Bearer ${_inMemoryToken}`;
            return formDataClient(originalRequest);
          }
        } catch { /* refresh failed */ }

        // Refresh failed or returned false — force logout
        _handleAuthFailure();
      }

      return Promise.reject(error);
    }
  );

  return formDataClient;
};

// ═══════════════════════════════════════════════════════════════════════
//  Visibility-Based Token Refresh
// ═══════════════════════════════════════════════════════════════════════

/**
 * When user returns to the tab (after sleep, switching apps, etc.),
 * check if token is expired and attempt proactive refresh.
 * This closes the gap where setTimeout timers are throttled/killed
 * by the browser for background tabs.
 */
function _initVisibilityRefresh() {
  if (typeof document === 'undefined') return;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;

    const expiryStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    if (!expiryStr) return;

    const expiryMs = parseInt(expiryStr, 10) * 1000;
    const now = Date.now();

    if (now >= expiryMs) {
      // Token already expired — try refresh immediately.
      // If refresh fails, don't force _handleAuthFailure here;
      // let React auth guards (ProtectedRoute / HomeRoute) handle the redirect
      // gracefully without broadcasting logout to other tabs.
      console.debug('[API Client] Tab visible + token expired → refreshing');
      _doRefresh();
    } else if (expiryMs - now < REFRESH_BUFFER_SECONDS * 1000) {
      // Within the buffer window — refresh proactively
      console.debug('[API Client] Tab visible + token expiring soon → refreshing');
      _doRefresh();
    } else {
      // Token still valid — ensure the proactive timer is running
      scheduleTokenRefresh();
    }
  });
}

_initVisibilityRefresh();

export { apiClient as default };